import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const EMPTY_FILTERS = {
  merchant: [], employee: [], department: [], category: [], policy: [], policy_status: [], status: [], gl: [],
  date_from: "", date_to: "", amount_min: "", amount_max: "",
};

export const LIST_KEYS = ["merchant", "employee", "department", "category", "policy", "policy_status", "status", "gl"];
export const URL_KEYS = [...LIST_KEYS, "date_from", "date_to", "amount_min", "amount_max", "q", "suggest_save"];

export const parseFiltersFromParams = (sp) => {
  const f = { ...EMPTY_FILTERS };
  const csv = (k) => (sp.get(k) ? sp.get(k).split(",").filter(Boolean) : []);
  LIST_KEYS.forEach((k) => { f[k] = csv(k); });
  if (sp.get("policy_status") === "triggered") f.policy_status = ["violation", "needs_info"];
  f.date_from = sp.get("date_from") || "";
  f.date_to = sp.get("date_to") || "";
  f.amount_min = sp.get("amount_min") || "";
  f.amount_max = sp.get("amount_max") || "";
  return f;
};

const PAGE_SIZE = 25;

// Server-driven expense list: tab + search + filters + sort + pagination.
export const useExpensesData = () => {
  const [tab, setTab] = useState("active");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [options, setOptions] = useState(null);
  const [views, setViews] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [sort, setSort] = useState("date");
  const [dir, setDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, tab_counts: {} });
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    window.addEventListener("aria:acted", refresh);
    return () => window.removeEventListener("aria:acted", refresh);
  }, [refresh]);

  const loadViews = useCallback(() => {
    api.get("/views").then((r) => setViews(r.data.views)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/expenses/filter-options").then((r) => setOptions(r.data)).catch(() => {});
    api.get("/employees").then((r) => setEmployeeOptions(r.data)).catch(() => {});
    loadViews();
  }, [loadViews]);

  useEffect(() => { setPage(1); }, [tab, debounced, filters]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ tab, sort, dir, page: String(page), page_size: String(PAGE_SIZE) });
    if (debounced) params.set("search", debounced);
    LIST_KEYS.forEach((k) => { if (filters[k].length) params.set(k, filters[k].join(",")); });
    ["date_from", "date_to", "amount_min", "amount_max"].forEach((k) => { if (filters[k]) params.set(k, filters[k]); });
    api.get(`/expenses?${params}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load expenses"))
      .finally(() => setLoading(false));
  }, [tab, debounced, filters, sort, dir, page, refreshKey]);

  const onFilterChange = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const clearFilterChip = (key) => {
    if (key === "date") setFilters((f) => ({ ...f, date_from: "", date_to: "" }));
    else if (key === "amount") setFilters((f) => ({ ...f, amount_min: "", amount_max: "" }));
    else setFilters((f) => ({ ...f, [key]: [] }));
  };

  const setSorting = (key, d) => { setSort(key); setDir(d); };

  const setSearchImmediate = (q) => { setSearch(q); setDebounced(q); };

  const applyView = (v) => {
    setFilters({ ...EMPTY_FILTERS, ...v.filters });
    toast.success(`View "${v.name}" applied`);
  };

  const saveView = async (name) => {
    const payload = {};
    LIST_KEYS.forEach((k) => { if (filters[k].length) payload[k] = filters[k]; });
    ["date_from", "date_to", "amount_min", "amount_max"].forEach((k) => { if (filters[k]) payload[k] = filters[k]; });
    try {
      await api.post("/views", { name, filters: payload });
      toast.success(`View "${name}" saved`);
      loadViews();
    } catch {
      toast.error("Could not save the view");
    }
  };

  const deleteView = async (id) => {
    try { await api.delete(`/views/${id}`); loadViews(); toast.success("View deleted"); } catch {}
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return {
    tab, setTab, search, setSearch, setSearchImmediate, filters, setFilters,
    onFilterChange, clearFilterChip, options, views, employeeOptions,
    sort, dir, setSorting, page, setPage, totalPages, data, loading, refresh,
    applyView, saveView, deleteView,
  };
};
